import React, { useCallback, useMemo, useState } from "react";
import type { ContentSource } from "@/services/sanity/types";
import { ContentStatusContext } from "./content-status-context";

interface ContentStatusEntry {
    message?: string;
    updatedAt: number;
}

export function ContentStatusProvider({ children }: { children: React.ReactNode }) {
    const [entries, setEntries] = useState<Record<string, ContentStatusEntry>>({});

    const clearStatus = useCallback((key: string) => {
        setEntries((currentEntries) => {
            if (!(key in currentEntries)) {
                return currentEntries;
            }

            const nextEntries = { ...currentEntries };
            delete nextEntries[key];
            return nextEntries;
        });
    }, []);

    const reportStatus = useCallback((key: string, source: ContentSource, message?: string) => {
        if (source !== "fallback") {
            setEntries((currentEntries) => {
                if (!(key in currentEntries)) {
                    return currentEntries;
                }

                const nextEntries = { ...currentEntries };
                delete nextEntries[key];
                return nextEntries;
            });
            return;
        }

        setEntries((currentEntries) => ({
            ...currentEntries,
            [key]: {
                message,
                updatedAt: Date.now(),
            },
        }));
    }, []);

    const activeFallbackMessage = useMemo(() => {
        const latestEntry = Object.values(entries).sort((left, right) => right.updatedAt - left.updatedAt)[0];
        return latestEntry?.message;
    }, [entries]);

    const value = useMemo(
        () => ({
            activeFallbackMessage,
            clearStatus,
            reportStatus,
        }),
        [activeFallbackMessage, clearStatus, reportStatus],
    );

    return <ContentStatusContext.Provider value={value}>{children}</ContentStatusContext.Provider>;
}
